<template>
  <div class="padding-10">
    <div class="d-flex align-center mb-4 flex-wrap">
      <div class="text-h6">Error Logs ({{ filteredList.length }})</div>

      <v-spacer></v-spacer>

      <div class="d-flex align-center mt-2 mt-sm-0 flex-wrap" style="gap: 8px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search module, message, user..."
          dense
          outlined
          clearable
          hide-details
          style="min-width: 240px;"
        ></v-text-field>

        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="dateRangeText"
              prepend-inner-icon="mdi-calendar"
              label="Date Range"
              dense
              outlined
              readonly
              clearable
              hide-details
              style="min-width: 240px;"
              v-bind="attrs"
              v-on="on"
              @click:clear="dateRange = []"
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateRange" no-title scrollable range :max="maxDate">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="dateRange = []">Clear</v-btn>
            <v-btn text color="primary" @click="dateMenu = false">OK</v-btn>
          </v-date-picker>
        </v-menu>

        <v-btn color="primary" depressed @click="fetchLogs" :loading="loading">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <v-card :elevation="$root.ele">
      <v-data-table
        :headers="headers"
        :items="filteredList"
        :loading="loading"
        item-key="id"
        class="templates-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100, -1],
        }"
        :items-per-page="25"
        sort-by="rawDate"
        :sort-desc="true"
        show-expand
        single-expand
      >
        <template v-slot:item.module="{ item }">
          <v-chip small color="indigo lighten-4" text-color="indigo darken-3" class="font-weight-medium">
            {{ item.module || 'unknown' }}
          </v-chip>
        </template>

        <template v-slot:item.message="{ item }">
          <span class="body-2">{{ item.errorDetails && item.errorDetails.message ? item.errorDetails.message : (item.description || '-') }}</span>
        </template>

        <template v-slot:item.createdBy="{ item }">
          <span>{{ item.createdBy || '-' }}</span>
        </template>

        <template v-slot:item.formattedDate="{ item }">
          <span>{{ item.formattedDate }}</span>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-3">
            <div v-if="item.description" class="body-2 mb-2">
              <strong>Description:</strong> {{ item.description }}
            </div>
            <div v-if="item.context" class="body-2 mb-2">
              <strong>Context:</strong>
              <pre class="context-block">{{ JSON.stringify(item.context, null, 2) }}</pre>
            </div>
            <div v-if="item.errorDetails && item.errorDetails.stack" class="body-2">
              <strong>Stack:</strong>
              <pre class="context-block">{{ item.errorDetails.stack }}</pre>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { errorLogs } from "../assets/js/firebase";

export default {
  name: "ErrorLogs",
  data() {
    return {
      loading: true,
      logs: [],
      search: "",
      dateMenu: false,
      dateRange: [],
      unsub: null,
    };
  },
  computed: {
    headers() {
      let headersList = [
        { text: "Module", value: "module" },
        { text: "Message", value: "message", sortable: false },
        { text: "User", value: "createdBy" },
        { text: "Date & Time", value: "formattedDate" },
      ];
      return headersList.map((h) => ({
        ...h,
        class: "text-capitalize",
        align: "start",
        sortable: h.sortable !== undefined ? h.sortable : true,
        divider: true,
      }));
    },
    filteredList() {
      let list = [...this.logs];

      const [start, end] = [...this.dateRange].sort();
      if (start) {
        const from = moment(start).startOf("day").valueOf();
        const to = moment(end || start).endOf("day").valueOf();
        list = list.filter((item) => item.rawDate >= from && item.rawDate <= to);
      }

      const query = (this.search || "").trim().toLowerCase();
      if (query) {
        const fields = ["module", "description", "createdBy"];
        list = list.filter((item) => {
          const directMatch = fields.some((f) =>
            String(item[f] || "").toLowerCase().includes(query)
          );
          const messageMatch = String(
            (item.errorDetails && item.errorDetails.message) || ""
          )
            .toLowerCase()
            .includes(query);
          const contextMatch = item.context
            ? JSON.stringify(item.context).toLowerCase().includes(query)
            : false;
          return directMatch || messageMatch || contextMatch;
        });
      }

      return list.sort((a, b) => b.rawDate - a.rawDate);
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    },
    dateRangeText() {
      return [...this.dateRange]
        .sort()
        .map((d) => moment(d).format("DD MMM YYYY"))
        .join(" - ");
    },
  },
  methods: {
    getRawTimestamp(timestamp) {
      if (!timestamp) return 0;
      if (timestamp.toDate && typeof timestamp.toDate === "function") {
        return timestamp.toDate().getTime();
      }
      if (timestamp.seconds) {
        return timestamp.seconds * 1000;
      }
      return new Date(timestamp).getTime() || 0;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "-";
      let dateObj = null;
      if (timestamp.toDate && typeof timestamp.toDate === "function") {
        dateObj = timestamp.toDate();
      } else if (timestamp.seconds) {
        dateObj = new Date(timestamp.seconds * 1000);
      } else {
        dateObj = new Date(timestamp);
      }
      const timeMs = new Date(dateObj).getTime();
      if (isNaN(timeMs)) return "-";
      return moment(dateObj).format("DD MMM YYYY, hh:mm:ss A");
    },
    listenLogs() {
      this.loading = true;
      const vm = this;

      if (vm.unsub) vm.unsub();

      vm.unsub = errorLogs.onSnapshot(
        (snap) => {
          vm.logs = snap.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                formattedDate: vm.formatTimestamp(data.createdAt),
                rawDate: vm.getRawTimestamp(data.createdAt),
              };
            })
            .sort((a, b) => b.rawDate - a.rawDate);
          vm.loading = false;
        },
        (err) => {
          console.error("[ErrorLogs] Error reading errorLogs:", err);
          vm.loading = false;
        }
      );
    },
    fetchLogs() {
      this.listenLogs();
    },
  },
  mounted() {
    this.listenLogs();
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
};
</script>

<style scoped>
.padding-10 {
  padding: 10px;
}
.context-block {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  margin-top: 4px;
}
</style>
